import { Injectable } from '@angular/core';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { PlanogramProductStack } from '../models/layout/planogram-product-stack';
import { LayoutUtilitiesService } from '../utilities/layout.utilities';
import { PlanogramService } from './planogram.service';
import { LayoutStateService } from './layout-state.service';

/**
 * Owns every recompute that derives numbers from the layout shape:
 * shelf width / height, drop totalFilledHeight, inter-shelf spacing,
 * stack & product position indices, and the two layoutwide breakdowns.
 *
 * Carved out as Step 2 of the LayoutService split. Mutators call these
 * methods after structural changes; the state service is only consulted
 * for the no-arg `updateLayoutBreakdown` / `updateLayoutDOSBreakdown`
 * variants that read the current layout snapshot.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutMetricsService {
  constructor(
    private planogramService: PlanogramService,
    private layoutUtils: LayoutUtilitiesService,
    private state: LayoutStateService
  ) {}

  updateProductPositions(
    stack: PlanogramProductStack,
    alsoSetHorizontalPosition: boolean = false
  ) {
    stack.products.forEach((p, index) => {
      p.vposition = index;
      if (alsoSetHorizontalPosition) p.position = stack.position;
    });
  }

  updateDropMetrics(layout: PlanogramLayout, dropIndex: number) {
    const shelfLipHeight = this.planogramService.getShelfLipHeight();
    const minShelfSpacing = this.planogramService.getShelfMinimumSpacing();
    const drop = layout.drops[dropIndex];
    if (!drop) return;

    drop.totalFilledHeight = this.layoutUtils.calculateDropTotalHeight(
      drop,
      shelfLipHeight,
      minShelfSpacing,
      layout
    );
    this.layoutUtils.calculateDropShelfSpacing(drop, minShelfSpacing);
  }

  updateShelfMetrics(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number
  ) {
    const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
    if (!shelf) return;

    shelf.filledWidth = this.layoutUtils.calculateShelfFilledWidth(
      layout,
      shelf
    );
    shelf.shelfHeight = this.layoutUtils.calculateMaxShelfHeight(shelf);
  }

  updateDropShelfSpacing(layout: PlanogramLayout, dropIndex: number) {
    const drop = layout.drops[dropIndex];
    if (!drop || drop.shelves.length <= 1) return; // No spacing needed for 0 or 1 shelf

    const defaultDropHeight = this.planogramService.getDropHeight();
    const defaultDropFooterHeight = this.planogramService.getDropFooterHeight();

    // Calculate the available vertical space by subtracting the footer height and total shelf filled height
    const availableSpace =
      (drop.height ?? defaultDropHeight) -
      (drop.footerHeight ?? defaultDropFooterHeight) -
      drop.totalFilledHeight;

    // Minimum spacing in cm
    const minSpacing = 0;

    if (availableSpace < 0) {
      // If no extra space available, set all shelves to minimum spacing
      drop.shelves.forEach((shelf, index) => {
        shelf.shelfSpacing = index === 0 ? minSpacing : 0;
      });
    } else {
      // The top shelf should always have minimum spacing from the top
      drop.shelves[0].shelfSpacing = minSpacing;

      // Calculate & distribute the remaining space evenly among gaps between shelves
      const numGaps = drop.shelves.length - 1; // Number of gaps between shelves
      const extraSpacingPerGap =
        numGaps > 0 ? Math.floor(availableSpace / numGaps) : 0;

      // Apply spacing to shelves
      drop.shelves.forEach((shelf, index) => {
        if (index > 0) {
          // Calculate spacing for other shelves
          shelf.shelfSpacing = minSpacing + extraSpacingPerGap;
        }
      });

      // Adjust bottom shelf spacing such that it aligns with the footer
      let accumulatedHeight = minSpacing; // Start with top shelf margin
      drop.shelves.forEach((shelf, index) => {
        accumulatedHeight += shelf.shelfHeight + shelf.shelfSpacing;
      });

      // Adjust last shelf's spacing to make sure it sticks to the footer
      const lastShelf = drop.shelves[drop.shelves.length - 1];
      const heightDifference =
        (drop.height ?? defaultDropHeight) -
        (drop.footerHeight ?? defaultDropFooterHeight) -
        accumulatedHeight;
      lastShelf.shelfSpacing += heightDifference;

      if (lastShelf.shelfSpacing < 0) {
        drop.shelves.forEach((shelf, index) => {
          shelf.shelfSpacing = 0;
        });
      }
    }
  }

  // Helper method to recalculate stack positions
  recalculateStackPositions(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number
  ) {
    const shelf = layout.drops[dropIndex]?.shelves[shelfIndex];
    if (!shelf) return;

    shelf.stacks.forEach((stack, index) => {
      stack.position = index;

      // Update horizontal position index for all products in this stack
      stack.products.forEach((p) => {
        p.position = index;
      });
    });
  }

  updateLayoutBreakdown(layout?: PlanogramLayout | null) {
    if (layout) {
      const breakdown = this.layoutUtils.calculateLayoutBreakdown(
        layout,
        this.state.planogramItems
      );
      this.state.publishBreakdown(breakdown);
    } else {
      layout = this.state.getLayoutValue();
      if (layout) {
        this.updateLayoutBreakdown(layout);
      } else {
        this.state.publishBreakdown(new Map());
      }
    }
  }

  updateLayoutDOSBreakdown(layout?: PlanogramLayout | null) {
    if (layout) {
      const breakdown = this.layoutUtils.calculateLayoutDOSBreakdown(
        layout,
        this.state.planogramItems
      );
      this.state.publishDOS(breakdown);
    } else {
      layout = this.state.getLayoutValue();
      if (layout) {
        this.updateLayoutDOSBreakdown(layout);
      } else {
        this.state.publishDOS(new Map());
      }
    }
  }

  updateLayoutDFacingsBreakdown(layout?: PlanogramLayout | null) {
    if (layout) {
      const breakdown = this.layoutUtils.calculateLayoutDFacingsBreakdown(
        layout,
        this.state.planogramItems
      );
      this.state.publishDFacings(breakdown);
    } else {
      layout = this.state.getLayoutValue();
      if (layout) {
        this.updateLayoutDFacingsBreakdown(layout);
      } else {
        this.state.publishDFacings(new Map());
      }
    }
  }

  updateLayoutCapacityBreakdown(layout?: PlanogramLayout | null) {
    if (layout) {
      const breakdown = this.layoutUtils.calculateLayoutCapacityBreakdown(
        layout,
        this.state.planogramItems
      );
      this.state.publishCapacity(breakdown);
    } else {
      layout = this.state.getLayoutValue();
      if (layout) {
        this.updateLayoutCapacityBreakdown(layout);
      } else {
        this.state.publishCapacity(new Map());
      }
    }
  }
}
